import * as THREE from 'three';

/**
 * Heightmap Terrain System
 *
 * Creates a single terrain mesh with:
 * - Procedural height variation using noise
 * - Height-based texture blending (sand -> grass -> dirt)
 * - Flat areas around specified pond positions
 * - Circular island shape with soft edges
 */

// Simple noise function for terrain generation
function noise2D(x, y, seed = 0) {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return (n - Math.floor(n)) * 2 - 1;
}

// Fractal noise for more natural terrain
function fractalNoise(x, y, octaves = 4, persistence = 0.5, scale = 1, seed = 42) {
  let total = 0;
  let frequency = scale;
  let amplitude = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    // Smoother interpolated noise
    const ix = Math.floor(x * frequency);
    const iy = Math.floor(y * frequency);
    const fx = x * frequency - ix;
    const fy = y * frequency - iy;

    // Smoothstep interpolation
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);

    const n00 = noise2D(ix, iy, seed + i);
    const n10 = noise2D(ix + 1, iy, seed + i);
    const n01 = noise2D(ix, iy + 1, seed + i);
    const n11 = noise2D(ix + 1, iy + 1, seed + i);

    const nx0 = n00 * (1 - sx) + n10 * sx;
    const nx1 = n01 * (1 - sx) + n11 * sx;
    const value = nx0 * (1 - sy) + nx1 * sy;

    total += value * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    frequency *= 2;
  }

  return total / maxValue;
}

// Vertex shader for terrain
const terrainVertexShader = `
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;
  varying float vHeight;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vHeight = position.z; // Height is stored in Z before rotation

    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Fragment shader with height-based texture blending
const terrainFragmentShader = `
  uniform sampler2D grassTexture;
  uniform sampler2D sandTexture;
  uniform sampler2D dirtTexture;
  uniform float grassTiling;
  uniform float sandTiling;
  uniform float dirtTiling;
  uniform float islandRadius;
  uniform vec3 lightDirection;
  uniform vec3 lightColor;

  // Cloud shadow uniforms
  uniform sampler2D cloudShadowTexture;
  uniform vec2 cloudShadowOffset;
  uniform float cloudShadowStrength;

  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;
  varying float vHeight;

  void main() {
    // Distance from center for edge blending
    float dist = length(vWorldPosition.xz);
    // Sharper edge like Wind Waker
    float edgeFade = 1.0 - smoothstep(islandRadius - 2.0, islandRadius, dist);

    // Discard pixels outside island
    if (edgeFade < 0.01) discard;

    // Sample textures with world-space tiling
    vec2 worldUv = vWorldPosition.xz;
    vec3 grass = texture2D(grassTexture, worldUv * grassTiling).rgb;
    vec3 sand = texture2D(sandTexture, worldUv * sandTiling).rgb;
    vec3 dirt = texture2D(dirtTexture, worldUv * dirtTiling).rgb;

    // Height-based blending
    // Below 0.3: sand (beaches, low areas)
    // 0.3-1.5: grass (main terrain)
    // Above 1.5: dirt (hills)
    float sandBlend = 1.0 - smoothstep(0.1, 0.5, vHeight);
    float dirtBlend = smoothstep(1.2, 2.0, vHeight);
    float grassBlend = 1.0 - sandBlend - dirtBlend;

    // Edge gets more sand (beach effect)
    float edgeSand = smoothstep(islandRadius - 12.0, islandRadius - 3.0, dist);
    sandBlend = max(sandBlend, edgeSand);
    grassBlend = grassBlend * (1.0 - edgeSand);

    // Blend colors
    vec3 terrainColor = grass * grassBlend + sand * sandBlend + dirt * dirtBlend;

    // Simple diffuse lighting
    float diffuse = max(dot(vNormal, normalize(lightDirection)), 0.0) * 0.6 + 0.4;
    vec3 litColor = terrainColor * diffuse * lightColor;

    // Cloud shadows - scale UV for smaller, more defined shadows
    if (cloudShadowStrength > 0.01) {
      vec2 shadowUv = vUv * 3.0 + cloudShadowOffset; // Scale up for smaller shadows
      float shadowAlpha = texture2D(cloudShadowTexture, shadowUv).a;
      float shadowEffect = mix(1.0 - cloudShadowStrength, 1.0, 1.0 - shadowAlpha);
      litColor *= shadowEffect;
    }

    gl_FragColor = vec4(litColor, edgeFade);
  }
`;

/**
 * Creates a heightmap terrain mesh
 * @param {Object} options Configuration options
 * @returns {THREE.Mesh} The terrain mesh
 */
export function createTerrain({
  radius = 55,
  segments = 128,
  maxHeight = 2.5,
  seed = 42,
  pondPositions = [],
  pondRadius = 3,
  grassTexture = null,
  sandTexture = null,
  dirtTexture = null,
  cloudShadowTexture = null
} = {}) {

  // Create plane geometry
  const geometry = new THREE.PlaneGeometry(radius * 2, radius * 2, segments, segments);
  const positions = geometry.attributes.position.array;
  const uvs = geometry.attributes.uv.array;

  // Store heights for shader
  const heights = new Float32Array(positions.length / 3);

  // Modify vertices based on heightmap
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const vertexIndex = i / 3;

    // Distance from center
    const dist = Math.sqrt(x * x + y * y);

    // Only modify vertices within island radius
    if (dist < radius) {
      // Base height from noise
      let height = fractalNoise(x * 0.02, y * 0.02, 4, 0.5, 1, seed) * maxHeight;

      // Add some larger hills
      height += fractalNoise(x * 0.008, y * 0.008, 2, 0.6, 1, seed + 100) * maxHeight * 0.5;

      // Flatten near edges (beach)
      const edgeFlatten = Math.max(0, 1 - Math.pow(dist / radius, 3));
      height *= edgeFlatten;

      // Flatten around ponds
      // Note: In PlaneGeometry before rotation, y maps to -z in world space
      let inPondArea = false;
      for (const pond of pondPositions) {
        const dx = x - pond.x;
        const dy = (-y) - pond.z; // y in plane -> -z in world after rotation
        const pondDist = Math.sqrt(dx * dx + dy * dy);

        if (pondDist < pondRadius * 3) {
          // Smooth flatten factor
          const flattenFactor = smoothstep(pondRadius * 3, pondRadius, pondDist);
          height *= (1 - flattenFactor);

          // Mark as pond area for depression
          if (pondDist < pondRadius * 1.5) {
            inPondArea = true;
            height = -0.1; // Depression for pond water
          }
        }
      }

      // Ensure minimum height above water level (but not in pond areas)
      if (!inPondArea) {
        height = Math.max(height, 0.05);
      }

      positions[i + 2] = height;
      heights[vertexIndex] = height;
    } else {
      // Outside island - set to 0 (will be discarded by shader)
      positions[i + 2] = 0;
      heights[vertexIndex] = 0;
    }
  }

  // Add height as custom attribute for shader
  geometry.setAttribute('height', new THREE.BufferAttribute(heights, 1));

  // Recompute normals after height modification
  geometry.computeVertexNormals();

  // Create default textures if not provided
  function createColorTexture(color) {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 4;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 4, 4);
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return tex;
  }

  const defaultGrass = grassTexture || createColorTexture('#5d9a2c');
  const defaultSand = sandTexture || createColorTexture('#e8d4a0');
  const defaultDirt = dirtTexture || createColorTexture('#8b5a2b');
  const defaultCloudShadow = createColorTexture('#ffffff');

  // Create material
  const material = new THREE.ShaderMaterial({
    uniforms: {
      grassTexture: { value: defaultGrass },
      sandTexture: { value: defaultSand },
      dirtTexture: { value: defaultDirt },
      grassTiling: { value: 0.08 },
      sandTiling: { value: 0.1 },
      dirtTiling: { value: 0.06 },
      islandRadius: { value: radius },
      lightDirection: { value: new THREE.Vector3(0.5, 1, 0.5).normalize() },
      lightColor: { value: new THREE.Color(1, 1, 1) },
      cloudShadowTexture: { value: cloudShadowTexture || defaultCloudShadow },
      cloudShadowOffset: { value: new THREE.Vector2(0, 0) },
      cloudShadowStrength: { value: 0 }
    },
    vertexShader: terrainVertexShader,
    fragmentShader: terrainFragmentShader,
    transparent: true,
    side: THREE.DoubleSide
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;

  return mesh;
}

// Helper smoothstep function
function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Get terrain height at a world position
 * @param {THREE.Mesh} terrain The terrain mesh
 * @param {number} x World X coordinate
 * @param {number} z World Z coordinate
 * @returns {number} Height at position
 */
export function getTerrainHeight(terrain, x, z) {
  const raycaster = new THREE.Raycaster();
  raycaster.set(new THREE.Vector3(x, 100, z), new THREE.Vector3(0, -1, 0));

  const intersects = raycaster.intersectObject(terrain);
  if (intersects.length > 0) {
    return intersects[0].point.y;
  }
  return 0;
}
